import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AttendanceTracker = ({
  currentAttendance,
  onClockIn,
  onClockOut,
  onStartBreak,
  onEndBreak,
  loading
}) => {
  const [elapsedTime, setElapsedTime] = useState('00:00:00');

  useEffect(() => {
    if (!currentAttendance?.clock_in_time) return;

    const interval = setInterval(() => {
      const start = new Date(currentAttendance.clock_in_time);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);

      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;

      setElapsedTime(
        `${String(hours)?.padStart(2, '0')}:${String(minutes)?.padStart(2, '0')}:${String(seconds)?.padStart(2, '0')}`
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [currentAttendance]);

  const isOnBreak = currentAttendance?.status === 'on_break';
  const isClockedIn = currentAttendance && !isOnBreak;

  return (
    <div className="card">
      <div className="text-center">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground mb-2">Time Clock</h2>
          <p className="text-muted-foreground">
            {format(new Date(), 'EEEE, MMMM d, yyyy')}
          </p>
        </div>

        {/* Timer Display */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-64 h-64 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 border-4 border-primary/30">
            <div className="text-center">
              <div className="text-5xl font-bold text-foreground mb-2">{elapsedTime}</div>
              <div className="text-sm text-muted-foreground">
                {currentAttendance ? (
                  isOnBreak ? (
                    <span className="flex items-center gap-1 justify-center text-warning">
                      <Icon name="Coffee" size={16} />
                      On Break
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 justify-center text-success">
                      <Icon name="CheckCircle" size={16} />
                      Clocked In
                    </span>
                  )
                ) : (
                  <span className="text-muted-foreground">Not Clocked In</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto">
          {!currentAttendance ? (
            <Button
              onClick={onClockIn}
              disabled={loading}
              size="lg"
              className="col-span-2"
            >
              {loading ? (
                <Icon name="Loader2" size={20} className="animate-spin" />
              ) : (
                <Icon name="LogIn" size={20} />
              )}
              <span>Clock In</span>
            </Button>
          ) : (
            <>
              {!isOnBreak ? (
                <>
                  <Button
                    onClick={onStartBreak}
                    disabled={loading}
                    variant="outline"
                    size="lg"
                  >
                    <Icon name="Coffee" size={20} />
                    <span>Start Break</span>
                  </Button>
                  <Button
                    onClick={onClockOut}
                    disabled={loading}
                    variant="destructive"
                    size="lg"
                  >
                    <Icon name="LogOut" size={20} />
                    <span>Clock Out</span>
                  </Button>
                </>
              ) : (
                <Button
                  onClick={onEndBreak}
                  disabled={loading}
                  size="lg"
                  className="col-span-2"
                >
                  <Icon name="Play" size={20} />
                  <span>End Break</span>
                </Button>
              )}
            </>
          )}
        </div>

        {/* Current Session Info */}
        {currentAttendance && (
          <div className="mt-8 p-4 bg-muted/30 rounded-lg">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground mb-1">Clock In</div>
                <div className="font-semibold text-foreground">
                  {format(new Date(currentAttendance.clock_in_time), 'h:mm a')}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Location</div>
                <div className="font-semibold text-foreground">
                  {currentAttendance?.location || 'Main Gym'}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Break Time</div>
                <div className="font-semibold text-foreground">
                  {currentAttendance?.break_duration_minutes || 0} min
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceTracker;